import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Unlock, AlertCircle } from 'lucide-react';
import Modal from './Modal';
import api from '../config/api';
import { useCafe } from '../context/CafeContext';
import { useLanguage } from '../context/LanguageContext';
import { showNotification } from './GlobalNotification';

/**
 * Open the till with a cash float, or close it by counting the drawer.
 * On close we show what the drawer should hold so the difference is obvious
 * before anything is committed.
 */
function CounterModal({ isOpen, onClose }) {
  const { counterSession, openCounter, closeCounter } = useCafe();
  const { t } = useLanguage();
  const isClosing = Boolean(counterSession);

  const [cashValue, setCashValue] = useState('');
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadSummary = useCallback(async () => {
    try {
      const response = await api.get('/api/counter/summary');
      setSummary(response.data);
    } catch (summaryError) {
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setCashValue('');
    setNotes('');
    setError('');

    if (isClosing) {
      loadSummary();
    }
  }, [isOpen, isClosing, loadSummary]);

  const countedCash = parseFloat(cashValue) || 0;
  const difference = summary ? countedCash - summary.expectedCash : 0;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (cashValue === '') {
      setError(isClosing ? t('Enter the cash counted in the drawer') : t('Enter the opening cash float'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (isClosing) {
        const result = await closeCounter(countedCash, notes);
        const diff = result.session?.difference ?? 0;
        showNotification(
          diff === 0 ? 'success' : 'warning',
          diff === 0
            ? t('Counter closed and the drawer balances.')
            : diff > 0
              ? t('Counter closed. Drawer is over by Rs {amount}.', { amount: Math.abs(diff).toFixed(2) })
              : t('Counter closed. Drawer is short by Rs {amount}.', { amount: Math.abs(diff).toFixed(2) })
        );
      } else {
        await openCounter(countedCash);
        showNotification('success', t('Counter opened. Token numbers start at 1.'));
      }

      onClose();
    } catch (submitError) {
      setError(submitError.response?.data?.message || t('Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isClosing ? t('Close Counter') : t('Open Counter')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {isClosing && summary && (
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('Opening float')}</span>
              <span className="font-medium">Rs {summary.session.openingCash.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('Cash sales')}</span>
              <span className="font-medium text-green-600">+ Rs {summary.cashSales.toFixed(2)}</span>
            </div>
            {/* An old due paid off in cash is money in this drawer, even though
                the sale itself belongs to an earlier day */}
            {summary.dueCollectedCash > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('Dues collected in cash')}</span>
                <span className="font-medium text-green-600">
                  + Rs {summary.dueCollectedCash.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">{t('Expenses paid out')}</span>
              <span className="font-medium text-red-600">- Rs {summary.totalExpenses.toFixed(2)}</span>
            </div>
            {/* Not spending - the money is still the cafe's - but it has left
                the drawer, so the count has to allow for it */}
            {summary.savingsDeposited > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('Moved to savings')}</span>
                <span className="font-medium text-emerald-600">
                  - Rs {summary.savingsDeposited.toFixed(2)}
                </span>
              </div>
            )}
            {summary.savingsWithdrawn > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('Taken from savings')}</span>
                <span className="font-medium text-emerald-600">
                  + Rs {summary.savingsWithdrawn.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold">
              <span>{t('Drawer should hold')}</span>
              <span>Rs {summary.expectedCash.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500 pt-1">
              <span>{t('Bank / digital sales (not in drawer)')}</span>
              <span>Rs {summary.bankSales.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>{t('Sold on due (not collected)')}</span>
              <span>Rs {(summary.dueSales ?? 0).toFixed(2)}</span>
            </div>
            {/* Not money, but the two figures that say how busy the session was
                - the same pair the mobile app shows before closing */}
            <div className="flex justify-between text-xs text-gray-500">
              <span>{t('Orders settled')}</span>
              <span>{summary.ordersCompleted ?? 0}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>{t('Tokens issued')}</span>
              <span>{summary.session?.tokenSequence ?? 0}</span>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isClosing ? t('Cash counted in drawer (Rs.)') : t('Opening cash float (Rs.)')}
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={cashValue}
            onChange={(e) => setCashValue(e.target.value)}
            className="input-field"
            placeholder="0.00"
            autoFocus
          />
          {!isClosing && (
            <p className="text-xs text-gray-500 mt-1">
              {t('The change you are starting the day with. Enter 0 if there is none.')}
            </p>
          )}
        </div>

        {isClosing && summary && cashValue !== '' && (
          <div className={`rounded-lg p-3 text-sm font-medium ${
            difference === 0
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
          }`}>
            {difference === 0
              ? t('The drawer balances exactly.')
              : difference > 0
                ? t('Drawer is over by Rs {amount}.', { amount: Math.abs(difference).toFixed(2) })
                : t('Drawer is short by Rs {amount}.', { amount: Math.abs(difference).toFixed(2) })}
          </div>
        )}

        {isClosing && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('Notes (optional)')}</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-field"
              placeholder={t('Anything worth recording about this shift')}
              maxLength={300}
            />
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">
            {t('Cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50 ${
              isClosing ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isClosing ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
            {submitting ? t('Working...') : isClosing ? t('Close Counter') : t('Open Counter')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default CounterModal;
